import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { BottomSideBorder } from '@/features/guardian/components/bottom-side-border';
import { levelColor } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import { memo, useCallback, useMemo, useState } from 'react';
import { Pressable, type LayoutChangeEvent } from 'react-native';
import MyKidCardAvatar, { type MyKidCardAvatarSource } from './avatar';
import {
  BOTTOM_BORDER_EDGE_WIDTH,
  BOTTOM_BORDER_STEPS,
  BOTTOM_BORDER_TIP_WIDTH,
} from './constants';

export type MyKidCardProps = {
  name: string;
  level: number;
  progress: number;
  avatarSource?: MyKidCardAvatarSource;
  onPress?: () => void;
};

function clampProgress(progress: number) {
  return Math.max(0, Math.min(1, progress));
}

function MyKidCard({ name, level, progress, avatarSource, onPress }: MyKidCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const normalizedProgress = clampProgress(progress);
  const percent = Math.round(normalizedProgress * 100);
  const color = useMemo(() => levelColor(level), [level]);
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width: nextWidth, height: nextHeight } = event.nativeEvent.layout;
    setMeasuredSize(prev =>
      prev.width === nextWidth && prev.height === nextHeight
        ? prev
        : { width: nextWidth, height: nextHeight },
    );
  }, []);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={name}
      disabled={!onPress}
      onPress={onPress}
      onLayout={handleLayout}
      style={({ pressed }) => [styles.card, pressed && onPress ? styles.cardPressed : null]}>
      <Stack align="center" paddingX={scale(12)} paddingY={scale(10)}>
        <MyKidCardAvatar
          color={color}
          progress={normalizedProgress}
          source={avatarSource ?? imgUser}
          hasAvatar={Boolean(avatarSource)}
        />

        <Text
          numberOfLines={1}
          variant="Font Guardian App/Body/Body 16 Semibold"
          style={styles.name}>
          {name}
        </Text>

        <Stack direction="row" align="center" justify="center" gap={scale(4)}>
          <Stack style={[styles.levelBadge, { backgroundColor: color }]}>
            <Text
              numberOfLines={1}
              fontSize={scale(8)}
              lineHeight={scale(11.2)}
              fontWeight={'500'}
              fontFamily={fontFamilies.openSans}
              style={styles.levelBadgeText}>
              {STRINGS.guardian.home.levelBadge.replace('{level}', String(level))}
            </Text>
          </Stack>
          <Text
            numberOfLines={1}
            variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
            style={styles.percent}>
            {percent}%
          </Text>
        </Stack>
      </Stack>

      <BottomSideBorder
        color={color}
        edgeWidth={BOTTOM_BORDER_EDGE_WIDTH}
        height={measuredSize.height}
        radius={scale(12)}
        steps={BOTTOM_BORDER_STEPS}
        tipWidth={BOTTOM_BORDER_TIP_WIDTH}
        width={measuredSize.width}
      />
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    backgroundColor: colors['0'],
    width: '100%',
    borderRadius: scale(12),
  },
  cardPressed: {
    opacity: 0.72,
  },
  name: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  levelBadge: {
    paddingHorizontal: scale(8),
    paddingVertical: scale(4),
    borderRadius: scale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  levelBadgeText: {
    color: colors['0'],
  },
  percent: {
    color: guardianColors['10'],
  },
}));

export default memo(MyKidCard);
